/**
 * Customer Reporting API client for the Customer Timescale Reporting feature.
 *
 * Fetches reporting data from the Timeseries API REST endpoint
 * at GET /v1/customers/{customer_id}/reporting.
 */

/** Metadata returned by the customer reporting endpoint */
export interface CustomerReportingMeta {
  customer_id: string
  customer_name: string
  system_count: number
  start: string
  stop: string
  frequency: string
  timezone: string
  row_count: number
  available_fields: string[]
}

/** Full response shape from the customer reporting endpoint */
export interface CustomerReportingResponse {
  meta: CustomerReportingMeta
  rows: Record<string, unknown>[]
}

/** Error thrown when the customer reporting API returns a non-OK response */
export class CustomerReportingApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message)
    this.name = "CustomerReportingApiError"
  }
}

/**
 * The base URL for the Timeseries API.
 * Falls back to the VITE_TIMESERIES_API_URL environment variable.
 */
const getDefaultBaseUrl = (): string => {
  return import.meta.env.VITE_TIMESERIES_API_URL ?? ""
}

/**
 * Fetches reporting data from the Timeseries API for a given customer.
 *
 * @param customerId - The customer ID to fetch reporting data for
 * @param start - ISO 8601 start timestamp
 * @param stop - ISO 8601 stop timestamp
 * @param frequency - Time bucket frequency: hourly | daily | weekly | monthly
 * @param fields - Array of Display_Field_Keys to include in the response
 * @param token - Bearer token for authorization
 * @param tz - IANA timezone for bucket alignment (defaults to "UTC")
 * @param siteId - Optional site ID to filter systems by site
 * @param baseUrl - Optional base URL override for the Timeseries API
 * @returns Typed CustomerReportingResponse with meta and rows
 * @throws CustomerReportingApiError on 400, 403, or 500 responses
 */
export async function fetchCustomerReportingData(
  customerId: string,
  start: string,
  stop: string,
  frequency: string,
  fields: string[],
  token: string,
  tz: string = "UTC",
  siteId?: string,
  baseUrl?: string,
): Promise<CustomerReportingResponse> {
  const base = baseUrl ?? getDefaultBaseUrl()

  const url = new URL(`${base}/customers/${customerId}/reporting`)
  url.searchParams.set("start", start)
  url.searchParams.set("stop", stop)
  url.searchParams.set("frequency", frequency)
  url.searchParams.set("fields", fields.join(","))
  url.searchParams.set("tz", tz)

  if (siteId) {
    url.searchParams.set("site_id", siteId)
  }

  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
    "X-Auth-Type": "better-auth",
  }

  const response = await fetch(url.toString(), {
    method: "GET",
    headers,
  })

  if (response.ok) {
    const data: CustomerReportingResponse = await response.json()
    return data
  }

  if (response.status === 400) {
    const errorBody = await response.json().catch(() => null)
    const message = errorBody?.detail ?? "Invalid request parameters"
    throw new CustomerReportingApiError(message, 400)
  }

  if (response.status === 403) {
    const errorBody = await response.json().catch(() => null)
    const message = errorBody?.detail ?? "You don't have access to this customer's reporting data."
    throw new CustomerReportingApiError(message, 403)
  }

  if (response.status === 404) {
    const errorBody = await response.json().catch(() => null)
    const message = errorBody?.detail ?? "No systems found for this customer."
    throw new CustomerReportingApiError(message, 404)
  }

  // All other errors (500, etc.)
  throw new CustomerReportingApiError("Something went wrong. Please try again later.", response.status)
}
